import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

//夜勤2直 brush-up(過渡期対策): バージョンゲート
//デプロイ毎に変わるビルドID(REACT_APP_BUILD_ID)を焼き込み、配信中の version.json と照合する。
//古い(=キャッシュ/常駐タブで残存した旧バンドル)を検知したら最新へリロードする。
// ・起動時: 入力前なので即リロード(作業ロス無し)。
// ・タブ復帰時(visibilitychange): 安全なルート(入力画面以外)なら即リロード、
//   入力中はバナーを表示し、一覧等の安全なルートへ移動した時に反映(作業ロス回避)。
// ・ループ防止: 同一サーバ版へ二重リロードしない(sessionStorage)。
// ・オフライン安全: version.json 取得失敗は無処理(現行バンドルで継続)。
// ・パフォーマンス: ポーリングせず、起動1回 + タブ復帰時(スロットル)のみの軽量fetch。
//   再レンダーは「更新あり」検知時のバナー1回のみで、入力/計算の hot path に影響しない。

//ビルド時に gen-version.js が .env.production.local へ設定。dev では未設定 → 'dev'(ゲート無効)。
const BUILD_ID: string = process.env.REACT_APP_BUILD_ID || 'dev'
const RELOAD_GUARD_KEY = 'rf_version_reload_target'
const CHECK_THROTTLE_MS = 60_000
//入力中(作業ロス回避)とみなすルート。これら以外を「安全」とする。
const INPUT_ROUTE_PREFIXES = ['/singleInput', '/multiInput']

const isSafePathname = (pathname: string): boolean =>
  !INPUT_ROUTE_PREFIXES.some((p) => pathname.startsWith(p))

const fetchServerVersion = async (): Promise<string | null> => {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' })
    if (!res.ok) return null
    const data = await res.json()
    return typeof data?.version === 'string' ? data.version : null
  } catch {
    return null //オフライン等は無処理(現行バンドルで継続)
  }
}

//同一サーバ版へ二重リロードしない(版不一致が一時的に続いてもループしない)
const reloadOnce = (target: string): void => {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === target) return
    sessionStorage.setItem(RELOAD_GUARD_KEY, target)
  } catch {
    //sessionStorage 不可環境でもリロード自体は行う
  }
  window.location.reload()
}

const VersionGate: FC = () => {
  const location = useLocation()
  const [pendingVersion, setPendingVersion] = useState<string | null>(null)
  const lastCheckRef = useRef<number>(0)

  const check = useCallback(async (forceReloadIfStale: boolean) => {
    const server = await fetchServerVersion()
    if (!server || server === BUILD_ID) return
    //古いバンドルが動作中
    if (forceReloadIfStale || isSafePathname(window.location.pathname)) {
      reloadOnce(server)
    } else {
      setPendingVersion(server) //入力中 → バナー表示し、安全なルートで反映
    }
  }, [])

  //起動時(マウント1回): 入力前なので即リロード
  useEffect(() => {
    check(true)
  }, [check])

  //タブ復帰時(スロットル)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const now = Date.now()
      if (now - lastCheckRef.current < CHECK_THROTTLE_MS) return
      lastCheckRef.current = now
      check(false)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [check])

  //入力画面から安全なルートへ移動したら保留中の更新を反映
  useEffect(() => {
    if (pendingVersion && isSafePathname(location.pathname)) {
      reloadOnce(pendingVersion)
    }
  }, [location.pathname, pendingVersion])

  if (!pendingVersion) return null
  return (
    <div
      onClick={() => window.location.reload()}
      role="button"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 2000,
        backgroundColor: '#1c82cf',
        color: '#fff',
        textAlign: 'center',
        padding: '8px 12px',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
      }}
    >
      新しいバージョンがあります。タップして更新してください
    </div>
  )
}

export default VersionGate
